import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export const userKeys = {
  me: () => ['me'] as const,
  user: (id: string) => ['user', id] as const,
  myAuctions: (tab: string) => ['my-auctions', tab] as const,
  subscriptions: () => ['my-subscriptions'] as const,
};

export function useMe(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.me(),
    queryFn: () => api.getMe(),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

export function useUserProfile(userId: string | undefined) {
  return useQuery({
    queryKey: userKeys.user(userId ?? ''),
    queryFn: () => api.getUserProfile(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useMyAuctions(
  tab: 'active' | 'won' | 'participating' | 'pending' | 'archived',
) {
  return useQuery({
    queryKey: userKeys.myAuctions(tab),
    queryFn: () => api.getMyAuctions(tab),
    staleTime: 30_000,
  });
}

export function useMySubscriptions() {
  return useQuery({
    queryKey: userKeys.subscriptions(),
    queryFn: () => api.getMySubscriptions(),
    staleTime: 60_000,
  });
}

export function useUserAuctions(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-auctions', userId ?? ''],
    queryFn: () => api.listUserAuctions(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });
}
export function useToggleSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sellerId: string) => api.toggleSubscription(sellerId),
    onSuccess: (_data, sellerId) => {
      qc.invalidateQueries({ queryKey: userKeys.user(sellerId) });
      qc.invalidateQueries({ queryKey: userKeys.subscriptions() });
    },
  });
}